import { Response, type Request } from 'miragejs'
import type { MirageServer } from '../../server.js'
import { createErrorResponse } from '../../utils.js'
import { getTodayDateTimeISO } from '@/utils/date/getter.js'
import { CONSTANTS } from '../constants.js'
import {
  ConversationStatus,
  ConversationPriority,
  MessageReceiptStatus,
  MessageDirection,
} from '@/layers/conversations/models/conversations.js'
import { ClientCustomerType } from '@/models/client.js'
import { randPhoneNumber } from '@ngneat/falso'
import type { ConversationInstance } from '../Conversation.js'
import { getInitials } from '@/utils/string.js'

/**
 * Creates a participant for a client in a conversation.
 * Extracts client information and creates a participant with the appropriate data.
 */
function createParticipantForClient(
  schema: MirageServer['schema'],
  conversation: ConversationInstance,
  clientId: number,
  now: string,
) {
  const client = schema.find('client', String(clientId))

  if (!client) {
    return null
  }

  const isOrganisation =
    client.attrs.customer_type !== ClientCustomerType.Private
  const firstName = isOrganisation
    ? (client.attrs.organization_name ?? '')
    : (client.attrs.first_name ?? '')
  const lastName = isOrganisation ? '' : (client.attrs.last_name ?? '')
  const initials = getInitials([firstName, lastName])

  const phoneNumbers = client.phoneNumbers?.models ?? []
  const primaryPhone = phoneNumbers[0]?.attrs?.number
  const phoneNumber =
    primaryPhone ??
    randPhoneNumber({
      countryCode: CONSTANTS.participant.defaultCountryCode,
    })

  return schema.create('participant', {
    conversation,
    participant_type: 'client',
    participant_internal_id: clientId,
    created_at: now,
    updated_at: now,
    info: {
      first_name: firstName,
      last_name: lastName,
      initials,
      phone_number: phoneNumber,
      avatar: '',
    },
  })
}

/**
 * Handler for creating a new conversation message
 * POST /conversations/messages
 */
export function handleCreateMessage(
  this: MirageServer,
  schema: MirageServer['schema'],
  request: Request,
) {
  if (CONSTANTS.conversationMessage.simulateError) {
    return createErrorResponse({
      status: 500,
      errors: [
        {
          detail: 'Service temporarily unavailable',
          code: 'internal_server_error',
        },
      ],
    })
  }

  const requestBody = JSON.parse(request.requestBody)
  const { client_id, content } = requestBody.data.attributes
  const now = getTodayDateTimeISO()

  let participant = schema.findBy('participant', {
    participant_internal_id: client_id,
  })

  let conversation

  if (!participant) {
    conversation = schema.create('conversation', {
      status: ConversationStatus.Open,
      priority: ConversationPriority.Normal,
      created_at: now,
      updated_at: now,
      last_message_at: now,
      no_of_messages: 0,
    })

    participant = createParticipantForClient(
      schema,
      conversation,
      client_id,
      now,
    )

    if (!participant) {
      conversation.destroy()
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Client with id ${client_id} not found` }],
      })
    }
  } else {
    conversation = participant.conversation

    if (!conversation) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: 'Conversation not found for participant' }],
      })
    }

    if (conversation.attrs.status === ConversationStatus.Resolved) {
      conversation = schema.create('conversation', {
        status: ConversationStatus.Open,
        priority: ConversationPriority.Normal,
        created_at: now,
        updated_at: now,
        last_message_at: now,
        no_of_messages: 0,
      })

      participant = createParticipantForClient(
        schema,
        conversation,
        client_id,
        now,
      )

      if (!participant) {
        conversation.destroy()
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `Client with id ${client_id} not found` }],
        })
      }
    }
  }

  const message = schema.create('message', {
    conversation,
    sender: participant,
    direction: MessageDirection.Outbound,
    message_type: 'text',
    content,
    created_at: now,
    updated_at: now,
  })

  // Create message receipt for the participant (simulating sent status)
  const receipt = schema.create('messageReceipt', {
    message,
    participant,
    status: 'sent',
    error_code: null,
    created_at: now,
    updated_at: now,
  })

  const currentMessageCount = conversation.no_of_messages || 0
  conversation.update({
    last_message_at: message.created_at,
    no_of_messages: currentMessageCount + 1,
  })

  return new Response(
    200,
    {},
    {
      data: {
        type: 'ConversationMessage',
        id: message.id,
        links: {
          self: `/api/conversations/messages/${message.id}`,
        },
        attributes: {
          external_id: null,
          message_type: message.attrs.message_type,
          content: message.attrs.content,
          direction: message.attrs.direction,
          created_at: message.attrs.created_at,
          updated_at: message.attrs.updated_at,
        },
        relationships: {
          sender: {
            data: {
              id: participant.id,
              type: 'ConversationParticipant',
            },
          },
          thread: {
            data: {
              id: conversation.id,
              type: 'ConversationThread',
            },
          },
          message_receipts: {
            data: [
              {
                id: receipt.id,
                type: 'ConversationMessageReceipt',
              },
            ],
          },
        },
      },
      included: [
        {
          type: 'ConversationMessageReceipt',
          id: receipt.id,
          attributes: {
            status: receipt.attrs.status,
            error_code: receipt.attrs.error_code,
            created_at: receipt.attrs.created_at,
            updated_at: receipt.attrs.updated_at,
          },
          relationships: {
            participant: {
              data: {
                id: participant.id,
                type: 'ConversationParticipant',
              },
            },
          },
        },
      ],
    },
  )
}

/**
 * Handler for sending messages to an existing thread
 * POST /conversations/threads/:id/messages
 */
export function handleSendMessageToThread(
  this: MirageServer,
  schema: MirageServer['schema'],
  request: Request,
) {
  if (CONSTANTS.conversationMessage.simulateError) {
    return createErrorResponse({
      status: 500,
      errors: [
        {
          detail: 'Service temporarily unavailable',
          code: 'internal_server_error',
        },
      ],
    })
  }

  const threadId = request.params.id
  const requestBody = JSON.parse(request.requestBody)
  const { content } = requestBody.data.attributes
  const now = getTodayDateTimeISO()

  const conversation = schema.find('conversation', threadId)

  if (!conversation) {
    return createErrorResponse({
      status: 404,
      errors: [{ detail: `Thread with id ${threadId} not found` }],
    })
  }

  if (conversation.attrs.status !== ConversationStatus.Open) {
    return createErrorResponse({
      status: 400,
      errors: [
        {
          detail: 'Thread must be OPEN to send messages',
          code: 'invalid_thread_status',
        },
      ],
    })
  }

  const participants = schema.where('participant', {
    conversationId: threadId,
  })
  const clientParticipant = participants.models.find(
    (p) => p.attrs.participant_type === 'client',
  )

  if (!clientParticipant) {
    return createErrorResponse({
      status: 400,
      errors: [
        {
          detail: 'No client participant found in thread',
          code: 'no_client_participant',
        },
      ],
    })
  }

  if (!clientParticipant.attrs.info?.phone_number) {
    return createErrorResponse({
      status: 400,
      errors: [
        {
          detail: 'Client participant has no phone number',
          code: 'no_phone_number',
        },
      ],
    })
  }

  const message = schema.create('message', {
    conversation,
    sender: clientParticipant,
    direction: MessageDirection.Outbound,
    message_type: 'text',
    content,
    created_at: now,
    updated_at: now,
  })

  // Create message receipt for the participant (simulating sent status)
  const receipt = schema.create('messageReceipt', {
    message,
    participant: clientParticipant,
    status: 'sent',
    error_code: null,
    created_at: now,
    updated_at: now,
  })

  const currentMessageCount = conversation.no_of_messages || 0
  conversation.update({
    last_message_at: message.created_at,
    no_of_messages: currentMessageCount + 1,
  })

  return new Response(
    200,
    {},
    {
      data: {
        type: 'ConversationMessage',
        id: message.id,
        links: {
          self: `/api/conversations/messages/${message.id}`,
        },
        attributes: {
          external_id: null,
          message_type: message.attrs.message_type,
          content: message.attrs.content,
          direction: message.attrs.direction,
          created_at: message.attrs.created_at,
          updated_at: message.attrs.updated_at,
        },
        relationships: {
          sender: {
            data: {
              id: clientParticipant.id,
              type: 'ConversationParticipant',
            },
          },
          thread: {
            data: {
              id: conversation.id,
              type: 'ConversationThread',
            },
          },
          message_receipts: {
            data: [
              {
                id: receipt.id,
                type: 'ConversationMessageReceipt',
              },
            ],
          },
        },
      },
      included: [
        {
          type: 'ConversationMessageReceipt',
          id: receipt.id,
          attributes: {
            status: receipt.attrs.status,
            error_code: receipt.attrs.error_code,
            created_at: receipt.attrs.created_at,
            updated_at: receipt.attrs.updated_at,
          },
          relationships: {
            participant: {
              data: {
                id: clientParticipant.id,
                type: 'ConversationParticipant',
              },
            },
          },
        },
      ],
    },
  )
}

/**
 * Handler for resending a failed message
 * POST /conversations/threads/:id/messages/:message_id/resend
 */
export function handleResendMessage(
  this: MirageServer,
  schema: MirageServer['schema'],
  request: Request,
) {
  const threadId = request.params.id
  const messageId = request.params.message_id
  const now = getTodayDateTimeISO()

  const conversation = schema.find('conversation', threadId)

  if (!conversation) {
    return createErrorResponse({
      status: 404,
      errors: [{ detail: `Thread with id ${threadId} not found` }],
    })
  }

  if (conversation.attrs.status !== ConversationStatus.Open) {
    return createErrorResponse({
      status: 409,
      errors: [
        {
          detail: 'Thread must be OPEN to resend messages',
          code: 'invalid_thread_status',
        },
      ],
    })
  }

  const message = schema.find('message', messageId)

  if (!message) {
    return createErrorResponse({
      status: 404,
      errors: [{ detail: `Message with id ${messageId} not found` }],
    })
  }

  if (message.conversationId !== threadId) {
    return createErrorResponse({
      status: 400,
      errors: [
        {
          detail: 'Message does not belong to this thread',
          code: 'message_thread_mismatch',
        },
      ],
    })
  }

  if (CONSTANTS.conversationMessage.simulateResendError) {
    return createErrorResponse({
      status: 502,
      errors: [
        {
          detail: 'Failed to resend message',
          code: 'resend_failed',
        },
      ],
    })
  }

  const failedReceipts = schema.where('messageReceipt', {
    messageId: messageId,
    status: MessageReceiptStatus.Failed,
  })

  failedReceipts.models.forEach((receipt) => {
    const participantId = receipt.attrs.participantId

    schema.create('messageReceipt', {
      message: message,
      participant: schema.find('participant', participantId),
      status: 'sent',
      error_code: null,
      created_at: now,
      updated_at: now,
    })

    receipt.destroy()
  })

  message.update({ updated_at: now })

  const sender = message.sender

  return new Response(
    200,
    {},
    {
      data: {
        type: 'ConversationMessage',
        id: message.id,
        links: {
          self: `/api/conversations/messages/${message.id}`,
        },
        attributes: {
          external_id: message.attrs.external_id ?? null,
          message_type: message.attrs.message_type,
          content: message.attrs.content,
          direction: message.attrs.direction,
          created_at: message.attrs.created_at,
          updated_at: message.attrs.updated_at,
        },
        relationships: {
          sender: {
            data: sender
              ? {
                  id: sender.id,
                  type: 'ConversationParticipant',
                }
              : null,
          },
          thread: {
            data: {
              id: conversation.id,
              type: 'ConversationThread',
            },
          },
        },
      },
    },
  )
}
